import _ from 'underscore';
import Immutable from 'immutable';

import MomentSupport from 'sharedreact/utils/MomentSupport';

function addPlaceHolderId (value: unknown, key: string, className: string): unknown {
  if (key === 'id' && value == null) {
    return _.uniqueId('placeholder_' + className);
  }
  return value;
}

function _normalizeClassName (className: string) {
  if (className.length){
    let firstCharUpperCase = className.charAt(0).toUpperCase();
    if (firstCharUpperCase !== className.charAt(0)) {
      return firstCharUpperCase + className.slice(1);
    }
  }

  return className;
}

class ClassFactory {
  private classes: {[name: string]: any};

  isInstanceOf (classObj: any) : (object: unknown) => boolean {
    return (object: unknown) => (object instanceof classObj);
  }

  isString (obj: unknown): boolean {
    return (typeof obj === 'string');
  }
  
  isListOf (isOfType: (obj: unknown) => boolean): (object: unknown) => boolean {
    return (obj: unknown) => (Immutable.isList(obj) && obj.findIndex(item => !isOfType(item)) === -1);
  }

  isMapOf (keyIsOfType: (obj: unknown) => boolean, valueIsOfType: (obj: unknown) => boolean): (object: unknown) => boolean {
    return (obj: unknown) => {
      if (Immutable.isMap(obj)) {
        for (const entry of obj.entries()) {
          if (!keyIsOfType(entry[0]) || !valueIsOfType(entry[1])) {
            return false;
          }
        }

        return true;
      }
      return false;
    }
  }

  constructor () {
    this.classes = {};
  }

  registerClassWithName (className: string, classObj: unknown): void {
    if (this.classes.hasOwnProperty(_normalizeClassName(className))) {
      console.error(`Error: reregister class ${className}`);
    }

    this.classes[_normalizeClassName(className)] = classObj;
  }

  createFromJson<ClassType>(json: object, classObj: any): ClassType {
    const serializedJson: Immutable.Collection<unknown, unknown> = this._createFromJson(json);

    if (serializedJson instanceof classObj) {
      return (serializedJson as ClassType);
    }

    throw new Error(`ClassCastException: unable to create instance of ${classObj.prototype.constructor.name} from ${JSON.stringify(json)}`);
  }

  createFromJsonWithTypeFunction<Type> (json: object, isOfType: (object: unknown) => boolean, typeName: string): Type {
    const serializedJson: Immutable.Collection<unknown, unknown> = this._createFromJson(json);

    if (isOfType(serializedJson)) {
      return (serializedJson as Type);
    }

    throw new Error(`ClassCastException: unable to create instance of ${typeName} from ${JSON.stringify(json)}`);
  }

  private _createFromJson (json: object | object[]): Immutable.Collection<unknown, unknown> {
    return Immutable.fromJS(json, (key, value) => {
      const createObject = (className: string, ...params: unknown[]) => {
        return this._createObject(className, ...params);
      };

      if (Immutable.isIndexed(value)) {
        return value.toList();
      } else if (value.get('objType') != null) {
        const className = value.get('objType') || '';
        if (typeof className === 'string') {
          const normalizedClassName: string = _normalizeClassName(className);
          return createObject(
            normalizedClassName,
            Immutable.Map(value)
              .map((value, key) => addPlaceHolderId(value, key, normalizedClassName))
              .map(MomentSupport.itemToMoment)
          );
        } else {
          return;
        }
      } else {
        return value.toOrderedMap();
      }
    });
  }

  private _createObject (className: string, ...params: unknown[]): unknown {
    if (this.classes.hasOwnProperty(className)) {
      return new this.classes[className](...params);
    } else {
      console.error(`Unknown class ${className}: class was not Registered`);
      return;
    }
  }
}

export default new ClassFactory();
